/**
 * DocumentList Component - Combined view of all RFP documents with expandable structure preview
 * Combines newly uploaded docs + previously uploaded docs in single sortable list
 * Each doc has clickable expand button to view extracted structure
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertCircle, Clock, Brain, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { Badge } from '@tracekit/core';

interface Section {
  number: string;
  name: string;
  level?: number;
  subsections?: Section[];
}

interface Criterion {
  id: string;
  name: string;
  display_name?: string;  // Enhanced name with 5-8 word description for short/coded names
  points?: number;
  description?: string;
}

interface Document {
  artifact_id: string;
  filename: string;
  file_size?: number;
  uploaded_at?: string;
  processing_status: string;
  chunks_count?: number;
  sections_extracted?: number;
  entities_count?: number;
  extracted_structure?: {
    sections: Section[];
    mandatory_criteria: Criterion[];
    rated_criteria: Criterion[];
    solicitation_constraints?: {
      page_limit?: number | null;
      prohibited_topics?: string[];
      admin_requirements?: Array<{ type?: string }>;
      status?: string;
      review_required?: boolean;
    };
  };
}

interface DocumentListProps {
  documents: Document[];
  selectedDocs?: string[];
  onToggleSelect?: (docId: string) => void;
  onRemove?: (docId: string) => void;
  onRefresh?: () => void;
  /** Queues full RFP re-ingest (parse → chunk → index → structure) with force=true on the server. */
  onReextractStructure?: (artifactId: string) => void | Promise<void>;
  /** When set, shows a spinner on that row’s re-extract control. */
  reextractingArtifactId?: string | null;
}

export function DocumentList({
  documents,
  selectedDocs = [],
  onToggleSelect,
  onRemove,
  onRefresh,
  onReextractStructure,
  reextractingArtifactId = null,
}: DocumentListProps) {
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleDocumentExpand = (docId: string) => {
    const newExpanded = new Set(expandedDocs);
    if (newExpanded.has(docId)) {
      newExpanded.delete(docId);
    } else {
      newExpanded.add(docId);
    }
    setExpandedDocs(newExpanded);
  };

  const toggleSectionExpand = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const renderSection = (section: Section, docId: string, path: string = ''): React.ReactNode => {
    const sectionId = `${docId}-${path}-${section.number}`;
    const isExpanded = expandedSections.has(sectionId);
    const hasSubsections = section.subsections && section.subsections.length > 0;

    return (
      <div key={sectionId} className="mb-1">
        <div
          className="flex items-center p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded cursor-pointer text-sm"
          onClick={() => hasSubsections && toggleSectionExpand(sectionId)}
        >
          {hasSubsections ? (
            <button className="mr-1 p-0">
              {isExpanded ? (
                <ChevronDown size={14} className="text-gray-600" />
              ) : (
                <ChevronRight size={14} className="text-gray-600" />
              )}
            </button>
          ) : (
            <div className="mr-1 w-3" />
          )}
          <span className="font-mono text-xs text-blue-700 dark:text-blue-400 mr-2 min-w-fit">
            {section.number}
          </span>
          <span className="text-gray-700 dark:text-gray-300">{section.name}</span>
        </div>

        {isExpanded && hasSubsections && (
          <div className="ml-4 border-l border-gray-300 dark:border-gray-600 pl-2">
            {section.subsections!.map((subsection) =>
              renderSection(subsection, docId, `${path}-${section.number}`)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {documents.map((doc) => {
        const isExpanded = expandedDocs.has(doc.artifact_id);
        const es = doc.extracted_structure;
        const nMandatory = es?.mandatory_criteria?.length ?? 0;
        const nRated = es?.rated_criteria?.length ?? 0;
        const solicitationConstraints = es?.solicitation_constraints;
        const prohibitedTopics = solicitationConstraints?.prohibited_topics || [];
        const adminRequirements = solicitationConstraints?.admin_requirements || [];
        const hasStructure =
          !!es &&
          ((es.sections?.length ?? 0) > 0 ||
            nMandatory > 0 ||
            nRated > 0);
        const isReady = doc.processing_status === 'completed';
        const rawStatus = (doc.processing_status || '').toLowerCase();
        const isStuck =
          !isReady &&
          (doc.chunks_count == null || doc.chunks_count === 0) &&
          ['pending', 'queued', 'waiting', 'failed', 'error'].includes(rawStatus);
        // API often keeps "queued" while the worker is already parsing/indexing; avoid implying nothing is happening.
        const statusLabel =
          isReady
            ? 'ready'
            : isStuck
              ? 'needs re-ingest'
              : ['queued', 'pending', 'waiting', 'ingested'].includes(rawStatus)
                ? 'processing'
                : doc.processing_status;
        const isReextracting = reextractingArtifactId === String(doc.artifact_id);

        return (
          <div
            key={doc.artifact_id}
            className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors overflow-hidden"
          >
            {/* Document Header - Always visible */}
            <div className="flex items-center justify-between p-3">
              {/* Left: Checkbox + Expand button + Filename */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {onToggleSelect && (
                  <input
                    type="checkbox"
                    checked={selectedDocs.includes(doc.artifact_id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelect(doc.artifact_id);
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                  />
                )}
                
                {hasStructure && (
                  <button
                    onClick={() => toggleDocumentExpand(doc.artifact_id)}
                    className="p-0 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-blue-600" />
                    ) : (
                      <ChevronRight size={16} className="text-blue-600" />
                    )}
                  </button>
                )}
                {!hasStructure && <div className="w-4" />}

                <FileText size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {doc.filename}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
                    {doc.chunks_count !== undefined && (
                      <span className='text-blue-600 dark:text-blue-400'>{doc.chunks_count} chunks</span>
                    )}
                    {doc.sections_extracted !== undefined && (
                      <span className="font-semibold text-purple-600 dark:text-purple-400">
                        {doc.sections_extracted} sections
                      </span>
                    )}
                    {doc.entities_count !== undefined && (
                      <span className='text-yellow-600 dark:text-yellow-400'>{doc.entities_count} entities</span>
                    )}
                    {hasStructure && (nMandatory > 0 || nRated > 0) && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500">·</span>
                        {nMandatory > 0 && (
                          <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                            {nMandatory} gated
                          </span>
                        )}
                        {nMandatory > 0 && nRated > 0 && (
                          <span className="text-gray-400 dark:text-gray-500">·</span>
                        )}
                        {nRated > 0 && (
                          <span className="text-amber-700 dark:text-amber-400 font-medium">
                            {nRated} rated
                          </span>
                        )}
                      </>
                    )}
                    {solicitationConstraints && (() => {
                      const nConstraints =
                        (solicitationConstraints.page_limit != null ? 1 : 0) +
                        prohibitedTopics.length +
                        adminRequirements.length;
                      return nConstraints > 0 ? (
                        <>
                          <span className="text-gray-400 dark:text-gray-500">·</span>
                          <span className="text-indigo-700 dark:text-indigo-400 font-medium">
                            {nConstraints} constraint{nConstraints === 1 ? '' : 's'}
                          </span>
                        </>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {/* Right: Re-ingest (stuck) / status icons + badge + remove */}
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                {isReady && hasStructure && !isReextracting && (
                  <Brain size={14} className="text-green-600 dark:text-green-400" />
                )}
                {(!isReady || isReextracting) && !isStuck && (
                  <Clock size={14} className="text-amber-600 dark:text-amber-400 animate-spin" />
                )}
                {isStuck && !isReextracting && (
                  <AlertCircle size={14} className="text-orange-500 dark:text-orange-400" />
                )}
                <Badge
                  variant={isReady && !isReextracting ? 'success' : isStuck ? 'warning' : 'default'}
                  className="text-xs"
                >
                  {isReextracting ? 're-extracting' : statusLabel}
                </Badge>
                {/* Stuck docs get an inline Re-ingest button — not buried in expand panel */}
                {isStuck && onReextractStructure && !isReextracting && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await onReextractStructure(String(doc.artifact_id));
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800/40 transition-colors"
                    title="Re-queue ingest job for this document"
                  >
                    <RefreshCw size={11} />
                    Re-ingest
                  </button>
                )}
                {isReextracting && (
                  <span className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    <Loader2 size={11} className="animate-spin" />
                    queuing…
                  </span>
                )}
                {onRemove && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRemove(String(doc.artifact_id));
                    }}
                    className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    title={`Remove ${doc.filename}`}
                    aria-label={`Remove ${doc.filename}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {isReextracting && (
              <div
                className="mx-3 mb-3 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-2 text-xs text-blue-800 dark:text-blue-200"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center gap-2 font-medium">
                  <Loader2 size={12} className="animate-spin shrink-0" />
                  Re-running ingest, indexing, and structure extraction for this document…
                </div>
                <div className="mt-1 text-blue-700/90 dark:text-blue-200/80">
                  Sections, criteria, and constraints will refresh here when the worker finishes.
                </div>
              </div>
            )}

            {/* Expandable Structure Preview */}
            {isExpanded && hasStructure && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/30 space-y-3 max-h-64 overflow-y-auto">
                {/* Sections */}
                {solicitationConstraints && (
                  <div className="rounded border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30 p-2">
                    <h4 className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 mb-1">
                      Solicitation Constraints
                    </h4>
                    <div className="flex flex-wrap gap-1 text-xs">
                      {solicitationConstraints.page_limit ? (
                        <span className="px-2 py-0.5 rounded bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-200">
                          {solicitationConstraints.page_limit} page cap
                        </span>
                      ) : null}
                      {prohibitedTopics.length > 0 ? (
                        <span className="px-2 py-0.5 rounded bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300">
                          {prohibitedTopics.length} prohibited topic{prohibitedTopics.length === 1 ? '' : 's'}
                        </span>
                      ) : null}
                      {adminRequirements.length > 0 ? (
                        <span className="px-2 py-0.5 rounded bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-200">
                          {adminRequirements.length} admin item{adminRequirements.length === 1 ? '' : 's'}
                        </span>
                      ) : null}
                      {solicitationConstraints.review_required ? (
                        <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300">
                          review recommended
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Sections */}
                {doc.extracted_structure!.sections && doc.extracted_structure!.sections.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Sections ({doc.extracted_structure!.sections.length})
                    </h4>
                    <div className="space-y-0 ml-2">
                      {doc.extracted_structure!.sections.map((section) =>
                        renderSection(section, doc.artifact_id)
                      )}
                    </div>
                  </div>
                )}

                {/* SMART CLASSIFICATION: Gated vs Rated based on points */}
                {(() => {
                  // Combine all criteria and classify by whether they have points > 0
                  const allMandatory = doc.extracted_structure!.mandatory_criteria || [];
                  const allRated = doc.extracted_structure!.rated_criteria || [];
                  
                  // GATED: mandatory_criteria + rated_criteria with no points (0, null, or undefined)
                  const gatedCriteria = [
                    ...allMandatory,
                    ...allRated.filter(c => !c.points || c.points === 0)
                  ];
                  
                  // RATED: only rated_criteria with points > 0
                  const trulyRatedCriteria = allRated.filter(c => c.points && c.points > 0);
                  
                  return (
                    <>
                      {/* Gated/Mandatory Criteria (pass-fail, no points) */}
                      {gatedCriteria.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-green-700 dark:text-green-300 mb-2">
                            Gated / Mandatory ({gatedCriteria.length})
                          </h4>
                          <div className="space-y-1 ml-2">
                            {gatedCriteria.map((crit) => (
                              <div key={crit.id} className="text-xs flex gap-2 p-1 bg-green-50 dark:bg-green-900/20 rounded">
                          <span className="font-mono font-bold text-green-700 dark:text-green-300 min-w-fit">
                            {crit.id}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 truncate" title={crit.description}>
                            {crit.display_name || crit.name}
                          </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rated/Scored Criteria (with points > 0) */}
                      {trulyRatedCriteria.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-2">
                            Rated / Scored ({trulyRatedCriteria.length})
                          </h4>
                          <div className="space-y-1 ml-2">
                            {trulyRatedCriteria.map((crit) => (
                              <div key={crit.id} className="text-xs flex gap-2 p-1 bg-orange-50 dark:bg-orange-900/20 rounded items-center">
                          <span className="font-mono font-bold text-orange-700 dark:text-orange-300 min-w-fit">
                            {crit.id}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 truncate flex-1" title={crit.description}>
                            {crit.display_name || crit.name}
                          </span>
                                <span className="font-mono text-orange-700 dark:text-orange-300 font-bold min-w-fit">
                                  {crit.points}pts
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Re-extract: server re-ingest with force + structure (see rfp_documents.re_ingest_rfp_document). */}
                {onReextractStructure && (
                  <button
                    type="button"
                    disabled={isReextracting}
                    title="Re-run ingestion and structure extraction for this file (does not delete the artifact)."
                    onClick={async (e) => {
                      e.stopPropagation();
                      await onReextractStructure(String(doc.artifact_id));
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-2 pt-2 border-t border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {isReextracting ? (
                      <Loader2 size={12} className="animate-spin shrink-0" />
                    ) : (
                      <RefreshCw size={12} className="shrink-0" />
                    )}
                    Re-extract structure
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
